import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Payroll, Employee } from '../types';

import { formatDate } from '../utils/dateUtils';

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    lastAutoTable: {
      finalY: number;
    };
  }
}

export interface PayslipData {
  payroll: Payroll;
  employee: Employee;
  companyInfo?: {
    name: string;
    address: string;
    tin: string;
    sssNumber: string;
    philHealthNumber: string;
    pagIbigNumber: string;
    secNumber?: string;
    birNumber?: string;
  };
}

// Helper function to generate payslip content
const generatePayslipContent = (doc: jsPDF, data: PayslipData, formatCurrencyForPDF: (amount: number) => string): void => {
  const { payroll, employee, companyInfo } = data;
  
  // Page setup - Compressed margins
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const contentWidth = pageWidth - (margin * 2);
  let yPosition = margin;
  
  // === COMPANY HEADER ===
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, yPosition, contentWidth, 25, 'F');
  doc.setDrawColor(0, 0, 0);
  doc.rect(margin, yPosition, contentWidth, 25);
  
  // Company name and details
  doc.setFont('courier', 'bold');
  doc.setFontSize(12);
  doc.text(companyInfo?.name || 'COMPANY NAME', pageWidth / 2, yPosition + 8, { align: 'center' });
  
  doc.setFont('courier', 'normal');
  doc.setFontSize(8);
  doc.text(companyInfo?.address || 'Company Address', pageWidth / 2, yPosition + 14, { align: 'center' });
  
  // Government registration numbers in smaller font
  doc.setFontSize(6);
  const govNumbers = [
    companyInfo?.tin || 'TIN: XXX-XXX-XXX-XXX',
    companyInfo?.sssNumber || 'SSS: XX-XXXXXXX-X',
    companyInfo?.philHealthNumber || 'PhilHealth: XX-XXXXXXXXX-X',
    companyInfo?.pagIbigNumber || 'Pag-IBIG: XXXX-XXXX-XXXX'
  ];
  doc.text(govNumbers.join(' | '), pageWidth / 2, yPosition + 20, { align: 'center' });
  
  yPosition += 30;
  
  // === PAYSLIP TITLE ===
  doc.setFont('courier', 'bold');
  doc.setFontSize(14);
  doc.text('PAYSLIP', pageWidth / 2, yPosition, { align: 'center' });
  
  yPosition += 8;
  
  // === EMPLOYEE INFORMATION SECTION ===
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.rect(margin, yPosition, contentWidth, 30);
  
  // Left column - Employee Details
  doc.setFont('courier', 'bold');
  doc.setFontSize(8);
  doc.text('EMPLOYEE INFORMATION', margin + 3, yPosition + 6);
  
  doc.setFont('courier', 'normal');
  doc.setFontSize(7);
  const leftColumnX = margin + 3;
  doc.text(`Name: ${employee.first_name} ${employee.last_name}`, leftColumnX, yPosition + 11);
  doc.text(`Employee ID: ${employee.id}`, leftColumnX, yPosition + 16);
  doc.text(`Position: ${employee.position || 'N/A'}`, leftColumnX, yPosition + 21);
  doc.text(`Department: ${employee.department || 'N/A'}`, leftColumnX, yPosition + 26);
  
  // Right column - Government Numbers
  const rightColumnX = pageWidth / 2 + 5;
  doc.setFont('courier', 'bold');
  doc.setFontSize(8);
  doc.text('GOVERNMENT NUMBERS', rightColumnX, yPosition + 6);
  
  doc.setFont('courier', 'normal');
  doc.setFontSize(7);
  doc.text(`TIN: ${employee.tin_number || 'XXX-XXX-XXX-XXX'}`, rightColumnX, yPosition + 11);
  doc.text(`SSS: ${employee.sss_number || 'XX-XXXXXXX-X'}`, rightColumnX, yPosition + 16);
  doc.text(`PhilHealth: ${employee.philhealth_number || 'XX-XXXXXXXXX-X'}`, rightColumnX, yPosition + 21);
  doc.text(`Pag-IBIG: ${employee.pagibig_number || 'XXXX-XXXX-XXXX'}`, rightColumnX, yPosition + 26);
  
  yPosition += 35;
  
  // === PAY PERIOD INFORMATION ===
  doc.setFillColor(245, 245, 245);
  doc.rect(margin, yPosition, contentWidth, 14, 'F');
  doc.setDrawColor(0, 0, 0);
  doc.rect(margin, yPosition, contentWidth, 14);
  
  doc.setFont('courier', 'bold');
  doc.setFontSize(8);
  doc.text('PAY PERIOD INFORMATION', margin + 3, yPosition + 6);
  
  doc.setFont('courier', 'normal');
  doc.setFontSize(7);
  const payPeriod = `Period: ${formatDate(payroll.pay_period_start)} to ${formatDate(payroll.pay_period_end)}`;
  const payDate = `Pay Date: ${formatDate(payroll.pay_period_end)}`;
  doc.text(payPeriod, margin + 3, yPosition + 11);
  doc.text(payDate, pageWidth - margin - 60, yPosition + 11);
  
  yPosition += 18;
  
  // === EARNINGS SECTION ===
  doc.setFillColor(230, 230, 230);
  doc.rect(margin, yPosition, contentWidth, 8, 'F');
  doc.setDrawColor(0, 0, 0);
  doc.rect(margin, yPosition, contentWidth, 8);
  
  doc.setFont('courier', 'bold');
  doc.setFontSize(8);
  doc.text('EARNINGS', pageWidth / 2, yPosition + 5, { align: 'center' });
  
  yPosition += 10;
  
  // Earnings table data with comprehensive breakdown
  const earningsData = [
    ['Description', 'Rate/Hours', 'Amount'],
    ['Basic Salary', `PHP ${(payroll.basic_salary / 22).toFixed(2)}/Hour`, formatCurrencyForPDF(payroll.basic_salary)],
    ['Overtime Pay (125%)', '0 hrs', 'P 0.00'],
    ['Night Differential (10%)', '0 hrs', 'P 0.00'],
    ['Holiday Pay', '0 days', 'P 0.00'],
    ['Allowances', '', 'P 0.00'],
    ['13th Month Pay (Prorated)', '', formatCurrencyForPDF(payroll.basic_salary / 12)],
    ['Other Earnings', '', 'P 0.00'],
    ['TOTAL GROSS EARNINGS', '', formatCurrencyForPDF(payroll.basic_salary + (payroll.basic_salary / 12))]
  ];
  
  autoTable(doc, {
    startY: yPosition,
    head: [earningsData[0]],
    body: earningsData.slice(1),
    theme: 'grid',
    headStyles: {
      fillColor: [200, 200, 200],
      textColor: [0, 0, 0],
      fontSize: 7,
      fontStyle: 'bold',
      halign: 'center',
      cellPadding: 1,
      font: 'courier'
    },
    bodyStyles: {
      fontSize: 6,
      cellPadding: 1,
      font: 'courier'
    },
    columnStyles: {
      0: { halign: 'left', cellWidth: contentWidth * 0.6, overflow: 'linebreak' },
      1: { halign: 'center', cellWidth: contentWidth * 0.2, overflow: 'linebreak' },
      2: { halign: 'center', cellWidth: contentWidth * 0.2, overflow: 'linebreak' }
    },
    tableWidth: contentWidth,
    margin: { left: margin, right: margin },
    didParseCell: function(data) {
      if (data.row.index === earningsData.length - 2) { // Total row
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [240, 240, 240];
      }
    }
  });
  
  yPosition = (doc as any).lastAutoTable.finalY + 5;
  
  // === DEDUCTIONS SECTION ===
  doc.setFillColor(230, 230, 230);
  doc.rect(margin, yPosition, contentWidth, 8, 'F');
  doc.setDrawColor(0, 0, 0);
  doc.rect(margin, yPosition, contentWidth, 8);
  
  doc.setFont('courier', 'bold');
  doc.setFontSize(8);
  doc.text('DEDUCTIONS', pageWidth / 2, yPosition + 5, { align: 'center' });
  
  yPosition += 10;
  
  // Deductions table data
  const deductionsData = [
    ['Description', 'Employee Share', 'Employer Share', 'Total'],
    ['SSS Contribution', 'P 0.00', 'P 0.00', 'P 0.00'],
    ['PhilHealth Contribution', 'P 0.00', 'P 0.00', 'P 0.00'],
    ['Pag-IBIG Contribution', formatCurrencyForPDF(100), formatCurrencyForPDF(100), 'P 0.00'],
    ['Withholding Tax (BIR)', 'P 0.00', '-', 'P 0.00'],
    ['SSS Loan', 'P 0.00', '-', 'P 0.00'],
    ['Pag-IBIG Loan', 'P 0.00', '-', 'P 0.00'],
    ['Cash Advance', 'P 0.00', '-', 'P 0.00'],
    ['Other Deductions', 'P 0.00', '-', 'P 0.00'],
    ['TOTAL DEDUCTIONS', formatCurrencyForPDF(payroll.total_deductions), '', formatCurrencyForPDF(payroll.total_deductions)]
  ];
  
  autoTable(doc, {
    startY: yPosition,
    head: [deductionsData[0]],
    body: deductionsData.slice(1),
    theme: 'grid',
    headStyles: {
      fillColor: [200, 200, 200],
      textColor: [0, 0, 0],
      fontSize: 7,
      fontStyle: 'bold',
      halign: 'center',
      cellPadding: 1,
      font: 'courier'
    },
    bodyStyles: {
      fontSize: 6,
      cellPadding: 1,
      font: 'courier'
    },
    columnStyles: {
      0: { halign: 'left', cellWidth: contentWidth * 0.4, overflow: 'linebreak' },
      1: { halign: 'center', cellWidth: contentWidth * 0.2, overflow: 'linebreak' },
      2: { halign: 'center', cellWidth: contentWidth * 0.2, overflow: 'linebreak' },
      3: { halign: 'center', cellWidth: contentWidth * 0.2, overflow: 'linebreak' }
    },
    tableWidth: contentWidth,
    margin: { left: margin, right: margin },
    didParseCell: function(data) {
      if (data.row.index === deductionsData.length - 2) { // Total row
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [240, 240, 240];
      }
    }
  });
  
  yPosition = (doc as any).lastAutoTable.finalY + 5;
  
  // === NET PAY SECTION ===
  doc.setFillColor(0, 0, 0);
  doc.rect(margin, yPosition, contentWidth, 16, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFont('courier', 'bold');
  doc.setFontSize(10);
  doc.text('NET PAY', margin + 5, yPosition + 7);
  
  doc.setFontSize(14);
  doc.text(formatCurrencyForPDF(payroll.net_pay), pageWidth - margin - 5, yPosition + 12, { align: 'right' });
  
  doc.setTextColor(0, 0, 0);
  yPosition += 20;
  
  // Leave balances section removed as requested
  yPosition += 5;
  
  // === FOOTER SECTION ===
  doc.setFont('courier', 'normal');
  doc.setFontSize(6);
  doc.setTextColor(100, 100, 100);
  
  const footerLines = [
    'This payslip is computer-generated and does not require a signature.',
    'Please verify all information and report any discrepancies to HR immediately.',
    `Generated on: ${new Date().toLocaleDateString('en-PH')} | Document ID: PS-${employee.id}-${new Date().getFullYear()}${(new Date().getMonth() + 1).toString().padStart(2, '0')}`,
    'Confidential Document - For Employee Use Only'
  ];
  
  footerLines.forEach((line, index) => {
    doc.text(line, pageWidth / 2, yPosition + (index * 4), { align: 'center' });
  });
  
  // Employee acknowledgment
  yPosition += 18;
  doc.setTextColor(0, 0, 0);
  doc.setFont('courier', 'normal');
  doc.setFontSize(7);
  doc.text('Employee Signature: ________________________', margin + 10, yPosition);
  doc.text('Date: ________________', pageWidth - margin - 60, yPosition);
};

export const payslipPdfService = {
  /**
   * Generate all employee payslips in a single PDF with page breaks
   */
  generateAllEmployeePayslipsPDF: (payrolls: Payroll[], employees: Employee[], companyInfo?: PayslipData['companyInfo']): void => {
    const doc = new jsPDF();
    
    // Helper function to format currency for PDF
    const formatCurrencyForPDF = (amount: number): string => {
      const formatted = new Intl.NumberFormat('en-PH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
      
      return `P ${formatted}`;
    };
    
    // Generate payslip for each employee
    payrolls.forEach((payroll, index) => {
      const employee = employees.find(emp => emp.id === payroll.employee_id);
      if (!employee) return;
      
      // Add new page for each payslip except the first one
      if (index > 0) {
        doc.addPage();
      }
      
      // Generate individual payslip content
      generatePayslipContent(doc, { payroll, employee, companyInfo }, formatCurrencyForPDF);
    });
    
    // Save the combined PDF
    const fileName = `All_Payslips_${formatDate(payrolls[0]?.pay_period_start || new Date().toISOString())}.pdf`;
    doc.save(fileName);
  },
  
  /**
   * Generate and download standard Philippine payslip PDF
   */
  generatePayslipPDF: (data: PayslipData): void => {
    const { payroll, employee } = data;
    const doc = new jsPDF();
    
    // Helper function to format currency for PDF
    const formatCurrencyForPDF = (amount: number): string => {
      const formatted = new Intl.NumberFormat('en-PH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
      
      return `P ${formatted}`;
    };
    
    // Generate payslip content
    generatePayslipContent(doc, data, formatCurrencyForPDF);
    
    // Save the PDF
    const fileName = `Payslip_${employee.first_name}_${employee.last_name}_${formatDate(payroll.pay_period_start)}.pdf`;
    doc.save(fileName);
  },
  
  /**
   * Generate and open standard Philippine payslip in new tab
   */
  openPayslipPDFInNewTab: (data: PayslipData): void => {
    const { payroll, employee } = data;
    const doc = new jsPDF();
    
    // Helper function to format currency for PDF
    const formatCurrencyForPDF = (amount: number): string => {
      const formatted = new Intl.NumberFormat('en-PH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
      
      return `P ${formatted}`;
    };
    
    // Generate payslip content
    generatePayslipContent(doc, data, formatCurrencyForPDF);
    
    // Open in new tab
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
  },
  
  /**
   * Generate all employee payslips in a single PDF and open in new tab
   */
  openAllEmployeePayslipsPDFInNewTab: (payrolls: Payroll[], employees: Employee[], companyInfo?: PayslipData['companyInfo']): void => {
    const doc = new jsPDF();
    
    // Helper function to format currency for PDF
    const formatCurrencyForPDF = (amount: number): string => {
      const formatted = new Intl.NumberFormat('en-PH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
      
      return `P ${formatted}`;
    };
    
    // Generate payslip for each employee
    payrolls.forEach((payroll, index) => {
      const employee = employees.find(emp => emp.id === payroll.employee_id);
      if (!employee) return;
      
      // Add new page for each payslip except the first one
      if (index > 0) {
        doc.addPage();
      }
      
      // Generate individual payslip content
      generatePayslipContent(doc, { payroll, employee, companyInfo }, formatCurrencyForPDF);
    });
    
    // Open in new tab
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
  }
};